import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectPersonas,
  selectEmployeeRecords,
  selectCompanyDocuments,
  selectWorkspaceUsers,
} from "../redux/features/agent/agentSlice";
import { selectHasNativeHRIS } from "../redux/features/appData/appDataSlice";
import type {
  Persona,
  EmployeeRecord,
  User,
  CompanyDocument,
} from "winslow-core";
import { getDefaultUserAvatar } from "winslow-core";
import { ValidatedImage } from "../components/common/ValidatedImage";
import { erGetCPValue } from "../components/app/app-employee-records/employeeRecordUtils";

export type MentionType =
  | "employee"
  | "persona"
  | "resource"
  | "employee-record"
  | "user"
  | "company-document";

export type MentionItem = {
  id: string;
  display: string;
  type: MentionType;
  icon?: string;
  avatarURL?: string;
  fallbackAvatar?: string;
  email?: string;
  subtitle?: string;
  originalData: Persona | EmployeeRecord | User | CompanyDocument;
};

export type MentionConfig = {
  trigger: string;
  data: MentionItem[];
  displayTransform: (id: string, display: string, type: MentionType) => string;
  style?: React.CSSProperties;
  className?: string;
};

const filterMentions = (
  items: MentionItem[],
  query: string,
  limit: number = 8
): MentionItem[] => {
  if (query.length < 2) return [];

  const lowercaseQuery = query.toLowerCase();

  return items
    .filter(
      (item) =>
        item.display.toLowerCase().includes(lowercaseQuery) ||
        (item.email && item.email.toLowerCase().includes(lowercaseQuery))
    )
    .slice(0, limit);
};

const getMentionItemsPerType = (
  allItems: MentionItem[],
  query: string,
  maxPerType: number = 3
): MentionItem[] => {
  if (query.length < 2) return [];

  const filtered = filterMentions(allItems, query, 24); // Get more items to distribute

  const groupedByType = filtered.reduce(
    (acc, item) => {
      if (!acc[item.type]) acc[item.type] = [];
      acc[item.type].push(item);
      return acc;
    },
    {} as Record<MentionType, MentionItem[]>
  );

  const result: MentionItem[] = [];

  // Add items from each type, respecting the maxPerType limit
  ["persona", "employee-record", "company-document"].forEach((type) => {
    const items = groupedByType[type as MentionType] || [];
    result.push(...items.slice(0, maxPerType));
  });

  return result.slice(0, 8); // Ensure we don't exceed 8 total items
};

export const useMentions = (): {
  allMentionItems: MentionItem[];
  getMentionData: (query?: string) => MentionItem[];
  getMentionConfig: (query?: string) => MentionConfig;
  renderMentionSuggestion: (
    suggestion: MentionItem,
    search: string,
    highlightedDisplay: React.ReactNode,
    index: number,
    focused: boolean
  ) => React.ReactNode;
  parseMentions: (text: string) => Array<{
    type: "text" | "mention";
    content: string;
    mentionData?: MentionItem;
  }>;
  hrisConnected: boolean;
} => {
  const personas = useSelector(selectPersonas);
  const employeeRecords = useSelector(selectEmployeeRecords);
  const workspaceUsers = useSelector(selectWorkspaceUsers);
  const companyDocuments = useSelector(selectCompanyDocuments);
  const hasNativeHRIS = useSelector(selectHasNativeHRIS);

  const allMentionItems = useMemo<MentionItem[]>(() => {
    const items: MentionItem[] = [];

    // Add personas
    if (personas) {
      personas.forEach((persona) => {
        items.push({
          id: `persona-${persona.id}`, // Prefix with type
          display: persona.name,
          type: "persona",
          avatarURL: "/icons/light-mode/persona_manager_off.svg",
          originalData: persona,
        });
      });
    }

    if (hasNativeHRIS && employeeRecords) {
      employeeRecords.forEach((employeeRecord) => {
        const firstName = erGetCPValue(employeeRecord, "system-firstName");
        const lastName = erGetCPValue(employeeRecord, "system-lastName");
        const title = erGetCPValue(employeeRecord, "system-title");
        const displayName = [firstName, lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
        const fallbackAvatar = getDefaultUserAvatar(
          displayName || employeeRecord.workEmail || ""
        );
        items.push({
          id: `employee-record-${employeeRecord.id}`, // Prefix with type
          display: displayName || employeeRecord.workEmail || "Employee",
          type: "employee-record",
          avatarURL: employeeRecord.avatarURL,
          fallbackAvatar,
          email: employeeRecord.workEmail,
          subtitle: title || undefined,
          originalData: employeeRecord,
        });
      });
    }

    // TODO: removing this for now - users should not be included in mentions
    // if (workspaceUsers) {
    //   workspaceUsers.forEach((workspaceUser) => {
    //     const fallbackAvatar = getDefaultUserAvatar(
    //       `${workspaceUser.firstName} ${workspaceUser.lastName}`
    //     );
    //     items.push({
    //       id: `user-${workspaceUser.id}`, // Prefix with type
    //       display: `${workspaceUser.firstName} ${workspaceUser.lastName}`,
    //       type: "user",
    //       avatarURL: workspaceUser.avatarURL || undefined,
    //       fallbackAvatar,
    //       email: workspaceUser.email,
    //       originalData: workspaceUser,
    //     });
    //   });
    // }

    if (companyDocuments) {
      companyDocuments.forEach((companyDocument) => {
        items.push({
          id: `company-document-${companyDocument.id}`, // Prefix with type
          display: companyDocument.name,
          type: "company-document",
          subtitle: companyDocument.description || undefined,
          originalData: companyDocument,
        });
      });
    }

    return items;
  }, [
    personas,
    employeeRecords,
    // workspaceUsers,
    companyDocuments,
    hasNativeHRIS,
  ]);

  const getMentionData = (query: string = ""): MentionItem[] => {
    const result = getMentionItemsPerType(allMentionItems, query);
    return result;
  };

  const getMentionConfig = (query: string = ""): MentionConfig => {
    const data = getMentionData(query);

    return {
      trigger: "@",
      data,
      displayTransform: (id: string, display: string, type: MentionType) => {
        // Remove emoji prefixes since we'll use proper icons in the suggestions
        return display;
      },
      style: {
        backgroundColor: "#f0f0f0",
        color: "#333",
        fontWeight: "400",
        borderRadius: "12px",
        padding: "2px 8px",
        fontSize: "inherit",
        display: "inline-flex",
        alignItems: "center",
        lineHeight: "1.4",
      },
    };
  };

  const getMentionTypeIcon = (type: MentionType): string => {
    switch (type) {
      case "employee":
        return ""; // No longer used for employees, we use avatars
      case "persona":
        return ""; // No longer used for personas, we use SVG
      case "user":
        return ""; // No longer used for users, we use avatars
      case "employee-record":
        return ""; // No longer used for employee records, we use avatars
      case "company-document":
        return ""; // No longer used for company documents, we use SVG
      case "resource":
        return "📄";
      default:
        return "•";
    }
  };

  const renderMentionSuggestion = (
    suggestion: MentionItem,
    search: string,
    highlightedDisplay: React.ReactNode,
    index: number,
    focused: boolean
  ): React.ReactNode => {
    return (
      <div
        key={suggestion.id}
        className="mention-suggestion-option"
        style={{
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          fontSize: "13px",
          color: "#333",
          backgroundColor: focused ? "#f8f8f8" : "transparent",
          transition: "background-color 0.1s ease",
        }}>
        {suggestion.type === "employee" ? (
          <ValidatedImage
            imageUrl={suggestion.avatarURL || ""}
            fallbackUrl={
              suggestion.fallbackAvatar ||
              getDefaultUserAvatar(suggestion.display)
            }
            alt={suggestion.display}
            className="mention-suggestion-avatar"
          />
        ) : suggestion.type === "persona" ? (
          <img
            src="/icons/light-mode/persona_manager_off.svg"
            alt="Persona"
            className="mention-suggestion-avatar"
          />
        ) : suggestion.type === "user" ? (
          <ValidatedImage
            imageUrl={suggestion.avatarURL || ""}
            fallbackUrl={
              suggestion.fallbackAvatar ||
              getDefaultUserAvatar(suggestion.display)
            }
            alt={suggestion.display}
            className="mention-suggestion-avatar"
          />
        ) : suggestion.type === "employee-record" ? (
          <ValidatedImage
            imageUrl={suggestion.avatarURL || ""}
            fallbackUrl={
              suggestion.fallbackAvatar ||
              getDefaultUserAvatar(suggestion.display)
            }
            alt={suggestion.display}
            className="mention-suggestion-avatar"
          />
        ) : suggestion.type === "company-document" ? (
          <img
            src="/icons/light-mode/note.svg"
            alt="Company Document"
            className="mention-suggestion-avatar"
            onError={(e) => {
              // Fallback for company document icon if not found
              e.currentTarget.style.display = "none";
              const fallback = document.createElement("div");
              fallback.textContent = "📋";
              fallback.style.cssText =
                "width: 20px; height: 20px; margin-right: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px;";
              e.currentTarget.parentNode?.insertBefore(
                fallback,
                e.currentTarget
              );
            }}
          />
        ) : suggestion.type === "resource" ? (
          <img
            src="/icons/light-mode/collection_resources_off.svg"
            alt="Resource"
            className="mention-suggestion-avatar"
            onError={(e) => {
              // Fallback for resource icon if not found
              e.currentTarget.style.display = "none";
              const fallback = document.createElement("div");
              fallback.textContent = "📄";
              fallback.style.cssText =
                "width: 20px; height: 20px; margin-right: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px;";
              e.currentTarget.parentNode?.insertBefore(
                fallback,
                e.currentTarget
              );
            }}
          />
        ) : (
          <div className="mention-suggestion-avatar">
            {getMentionTypeIcon(suggestion.type)}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: "500" }}>{highlightedDisplay}</div>
          {suggestion.email && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
              {suggestion.email}
            </div>
          )}
          {suggestion.subtitle && (
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                marginTop: suggestion.email ? "2px" : "2px",
              }}>
              {suggestion.subtitle}
            </div>
          )}
        </div>
      </div>
    );
  };

  const parseMentions = (
    text: string
  ): Array<{
    type: "text" | "mention";
    content: string;
    mentionData?: MentionItem;
  }> => {
    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({
          type: "text" as const,
          content: text.slice(lastIndex, match.index),
        });
      }

      // Add mention
      const display = match[1];
      const id = match[2]; // This will now include the prefix (e.g., "employee-123")

      const mentionItem = allMentionItems.find(
        (item: MentionItem) => item.id === id
      );

      parts.push({
        type: "mention" as const,
        content: display,
        mentionData: mentionItem,
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: "text" as const,
        content: text.slice(lastIndex),
      });
    }

    return parts;
  };

  return {
    allMentionItems,
    getMentionData,
    getMentionConfig,
    renderMentionSuggestion,
    parseMentions,
    hrisConnected: hasNativeHRIS,
  };
};
